/**
 * One item of the announcement.
 *
 * Icon, colour and privileges are shared across languages; only the title and body change with
 * the language, which is why those two labels carry the active language in their text — without
 * it people edit the wrong language's field and only notice after publishing.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { useState } from 'react';
import type { JSX } from 'react';
import type { TenantFlag } from '../../../Hooks/useTenantFlags';
import type { Language, WhatsNewFormItem } from '../../../Types/WhatsNewForm.types';
import { normalizeHexInput, toPickerValue } from '../../../Utils/hexColor';
import { IconGlyph, IconPicker } from '@/Shared/components/IconPicker/IconPicker';
import { isKnownIcon } from '@/Shared/icons/iconCatalog';
import { PrivilegesPicker } from './PrivilegesPicker';
import { IconButton } from '@/Shared/components/IconButton/IconButton';

const LANGUAGE_NAMES: Record<Language, string> = {
  pt: 'Português',
  en: 'Inglês',
  es: 'Espanhol',
};

const cardStyle = css`
  background: var(--color-surface-sunken);
  border-radius: var(--radius-card);
  padding: var(--space-m);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const iconBoxStyle = css`
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: var(--radius-m);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const headerTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const headerTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const headerValueStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const spacerStyle = css`
  flex: 1;
`;

const rowStyle = css`
  display: flex;
  gap: var(--space-m);
  flex-wrap: wrap;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  position: relative;
`;

const triggerInvalidStyle = css`
  border-color: var(--color-danger);
`;

const triggerTextStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const fieldErrorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
  text-wrap: pretty;
`;

const labelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const triggerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  height: 36px;
  padding: 0 var(--space-sm);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

/*
  The swatch IS the picker: a native `input[type=color]` stripped of its chrome. Native because the
  OS picker already has an eyedropper and the recent-colours list, which a hand-rolled panel would
  not, and because it costs no dependency.
*/
const swatchStyle = css`
  width: 44px;
  height: 36px;
  flex: 0 0 auto;
  padding: 0;
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  /* Without these the browser paints its own inset swatch and padding inside our box. */
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: calc(var(--radius-control) - 1px);
  }

  &::-moz-color-swatch {
    border: none;
    border-radius: calc(var(--radius-control) - 1px);
  }
`;

const colorRowStyle = css`
  display: flex;
  gap: var(--space-s);
  align-items: flex-end;
`;

const hexInputStyle = css`
  width: 110px;
  height: 36px;
  padding: 0 var(--space-s);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  background: var(--color-surface);
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const textareaStyle = css`
  width: 100%;
  padding: var(--space-s);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const chipsStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const chipStyle = css`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: var(--radius-round);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
`;

const countStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

export interface ItemCardProps {
  item: WhatsNewFormItem;
  index: number;
  language: Language;
  flags: TenantFlag[];
  isLoadingFlags: boolean;
  isMissingKey: boolean;
  environmentKey?: string | null;
  canRemove: boolean;
  onChange: (index: number, next: WhatsNewFormItem) => void;
  onRemove: (index: number) => void;
  onOpenBU: () => void;
}

export const ItemCard = ({
  item,
  index,
  language,
  flags,
  isLoadingFlags,
  isMissingKey,
  environmentKey,
  canRemove,
  onChange,
  onRemove,
  onOpenBU,
}: ItemCardProps): JSX.Element => {
  const [isIconOpen, setIsIconOpen] = useState(false);
  const [isPrivilegesOpen, setIsPrivilegesOpen] = useState(false);

  const privileges = item.privileges ?? [];
  const languageName = LANGUAGE_NAMES[language];

  const update = (patch: Partial<WhatsNewFormItem>) => {
    onChange(index, { ...item, ...patch });
  };

  /*
    An icon that came from pasted JSON can name something this build does not have. It is reported
    here rather than only at the step gate, because this is the field that fixes it.
  */
  const isIconInvalid = item.icon.trim().length > 0 && !isKnownIcon(item.icon);

  const updateTranslation = (field: 'title' | 'body', value: string) => {
    onChange(index, {
      ...item,
      translations: {
        ...item.translations,
        [language]: { ...item.translations[language], [field]: value },
      },
      // The primary language also feeds the flat title/body the BFF stores.
      ...(language === 'pt' ? { [field]: value } : {}),
    });
  };

  return (
    <article className={cardStyle}>
      <div className={headerStyle}>
        <span className={iconBoxStyle}>
          <IconGlyph value={item.icon} size={20} color={item.color} />
        </span>
        <div className={headerTextStyle}>
          <span className={headerTitleStyle}>Item {index + 1}</span>
          <span className={headerValueStyle}>{item.icon || 'sem ícone'}</span>
        </div>
        <div className={spacerStyle} />
        {canRemove ? (
          <IconButton
            icon='Misc.Trash'
            size='sm'
            aria-label={`Remover item ${index + 1}`}
            onClick={() => {
              onRemove(index);
            }}
          />
        ) : null}
      </div>

      <div className={rowStyle}>
        <div className={fieldStyle}>
          <span className={labelStyle}>Ícone</span>
          <button
            type='button'
            className={`${triggerStyle} ${isIconInvalid ? triggerInvalidStyle : ''}`}
            onClick={() => {
              setIsIconOpen(current => !current);
            }}
          >
            <IconGlyph value={item.icon} size={18} color={item.color} />
            <span className={triggerTextStyle}>{item.icon || 'Escolher'}</span>
          </button>
          {isIconInvalid ? (
            <span className={fieldErrorStyle}>Este ícone não existe. Escolha um da lista.</span>
          ) : null}
          {isIconOpen ? (
            <IconPicker
              value={item.icon}
              color={item.color}
              onSelect={icon => {
                update({ icon });
              }}
              onClose={() => {
                setIsIconOpen(false);
              }}
            />
          ) : null}
        </div>

        <div className={fieldStyle}>
          <span className={labelStyle}>Cor</span>
          <div className={colorRowStyle}>
            <input
              type='color'
              className={swatchStyle}
              value={toPickerValue(item.color)}
              aria-label={`Escolher a cor do item ${index + 1}`}
              onChange={event => {
                update({ color: event.target.value.toUpperCase() });
              }}
            />
            <input
              className={hexInputStyle}
              value={item.color}
              aria-label={`Cor do item ${index + 1} em hexadecimal`}
              spellCheck={false}
              onChange={event => {
                update({ color: normalizeHexInput(event.target.value) });
              }}
            />
          </div>
        </div>
      </div>

      <div className={fieldStyle}>
        <span className={labelStyle}>Título · {languageName}</span>
        <textarea
          className={textareaStyle}
          rows={1}
          value={item.translations[language].title}
          onChange={event => {
            updateTranslation('title', event.target.value);
          }}
        />
      </div>

      <div className={fieldStyle}>
        <span className={labelStyle}>Descrição · {languageName}</span>
        <textarea
          className={textareaStyle}
          rows={3}
          value={item.translations[language].body}
          onChange={event => {
            updateTranslation('body', event.target.value);
          }}
        />
      </div>

      <div className={fieldStyle}>
        <span className={labelStyle}>Privileges · flags do Flagsmith</span>
        <button
          type='button'
          className={triggerStyle}
          onClick={() => {
            setIsPrivilegesOpen(current => !current);
          }}
        >
          {privileges.length === 0 ? (
            <span className={countStyle}>Sem restrição — todos veem</span>
          ) : (
            <span className={chipsStyle}>
              {privileges.map(flag => (
                <span key={flag} className={chipStyle}>
                  {flag}
                </span>
              ))}
              <span className={countStyle}>{privileges.length}</span>
            </span>
          )}
        </button>
        {isPrivilegesOpen ? (
          <PrivilegesPicker
            selected={privileges}
            flags={flags}
            isLoading={isLoadingFlags}
            isMissingKey={isMissingKey}
            environmentKey={environmentKey}
            onOpenBU={onOpenBU}
            onToggle={flagName => {
              update({
                privileges: privileges.includes(flagName)
                  ? privileges.filter(entry => entry !== flagName)
                  : [...privileges, flagName],
              });
            }}
            onClose={() => {
              setIsPrivilegesOpen(false);
            }}
          />
        ) : null}
      </div>
    </article>
  );
};
